import { FC, useState, useMemo, useEffect, useRef, useContext, useLayoutEffect, useSyncExternalStore } from 'react';
import { Column } from 'ts/shared/interfaces/Generic';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faMinus,
  faPlus,
  faSort,
  faSortDown,
  faSortUp,
} from '@fortawesome/free-solid-svg-icons';
import { getIcon, getPayment, getStatus } from 'constants/mo';
import { faCcVisa } from '@fortawesome/free-brands-svg-icons';
import { parse, addDays, isValid } from 'date-fns';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  SortingState,
  ColumnFiltersState,
  flexRender,
  createColumnHelper,
  ColumnResizeMode,
} from '@tanstack/react-table';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { MoContext } from 'contexts/MoContext';
import { matchesMainFilter } from 'utils/shared/utils';
import InfiniteScroll from 'react-infinite-scroller';
import { matchesTableDataSearchFilters, SearchFilters } from 'utils/shared/my-order';
import { getOrdersListFilter, getOrdersListFilterVersion, subscribeOrdersListFilter } from './Assistant/ordersActions';

interface TableTanStackProps {
  data: any[];
  columns: Column[];
  onDoubleClick?: (id: number) => void;
  onClick?: (id: number) => void;
  activeId?: number;
  hideSort?: boolean;
  globalFilter: string;
  enableColumnResize?: boolean;
  tableId?: string;
}

const getOverflowParent = (element: HTMLElement, fallback: HTMLElement | null) => {
  let parent = element.parentElement;

  while (parent && parent !== document.body) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';

    if (isScrollable && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return fallback;
};

const keepRowInView = (rowEl: HTMLElement, fallbackContainer: HTMLElement | null) => {
  const container = getOverflowParent(rowEl, fallbackContainer);
  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const rowRect = rowEl.getBoundingClientRect();
  const pad = rowRect.height;
  const thead = rowEl.closest('table')?.tHead?.getBoundingClientRect();
  const stickyHeader =
    thead && thead.bottom > containerRect.top && thead.top < containerRect.top + 1
      ? Math.max(0, thead.bottom - containerRect.top)
      : 0;
  const topBound = containerRect.top + stickyHeader + pad;
  const bottomBound = containerRect.bottom - pad;

  if (rowRect.bottom > bottomBound) {
    container.scrollTop += rowRect.bottom - bottomBound;
  } else if (rowRect.top < topBound) {
    container.scrollTop -= topBound - rowRect.top;
  }

  const rowAfter = rowEl.getBoundingClientRect();
  const containerAfter = container.getBoundingClientRect();
  const visibleTop = containerAfter.top + stickyHeader;
  if (rowAfter.bottom > containerAfter.bottom || rowAfter.top < visibleTop) {
    rowEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
};

const TableTanStack: FC<TableTanStackProps> = ({
  columns,
  onDoubleClick = () => { },
  enableColumnResize = true,
  tableId = 'default',
  ...props
}) => {
  const [t] = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const listFilterVersion = useSyncExternalStore(subscribeOrdersListFilter, getOrdersListFilterVersion);
  const [globalFilter, setGlobalFilter] = useState('');
  const [showMore, setShowMore] = useState<string[]>([]);
  const [filterVisibility, setFilterVisibility] = useState<Record<string, boolean>>({});
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
  const [isResizing, setIsResizing] = useState(false);
  const columnHelper = createColumnHelper<any>();
  const tableRef = useRef<HTMLTableElement>(null);
  const { setSort, setVisibleRows, mainFilter, searchModel } = useContext(MoContext);
  const count = 50;
  const [total, setTotal] = useState(count);
  const [hasMore, setHasMore] = useState(true);
  const [debouncedSearchFilters, setDebouncedSearchFilters] = useState<SearchFilters>({});
  const wrapperRef = useRef<HTMLDivElement>(null);
  const activeIdRef = useRef(props.activeId);
  activeIdRef.current = props.activeId;

  const localStorageKey = `table-column-sizes-${tableId}`;

  const handleSortingChange = (updaterOrValue: any) => {
    setSorting(updaterOrValue);

    const newSorting =
      typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;

    if (newSorting.length > 0) {
      const primarySort = newSorting[0];
      const field = primarySort.id;
      const direction = primarySort.desc ? 'desc' : 'asc';

      setSort({ field, direction });
    }
  };

  const customGlobalFilterFn = useMemo(() => {
    return (row: any) => {
      const { orderRow, clx } = row.original;
      return matchesMainFilter(orderRow, clx, mainFilter);
    };
  }, [mainFilter]);

  useEffect(() => {
    setGlobalFilter(props.globalFilter);
    setTimeout(() => updateVisibleRows(), 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.globalFilter, props.data, sorting, columnFilters, debouncedSearchFilters]);

// eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchModel) {
      const filters: SearchFilters = {
        orderSupplierPNR: searchModel.orderSupplierPNR,
        orderBackOfficeId: searchModel.orderBackOfficeId,
        orderPaxName: searchModel.orderPaxName,
        orderItem: searchModel.orderItem,
      };

      const timer = setTimeout(() => {
        setDebouncedSearchFilters(filters);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setDebouncedSearchFilters({});
    }
  }, [searchModel]);

  useEffect(() => {
    setTimeout(() => updateVisibleRows(), 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainFilter]);

// eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTotal(count);
  }, [props.data.length, mainFilter, globalFilter, columnFilters.length]);

  useEffect(() => {
    try {
      const savedSizes = localStorage.getItem(localStorageKey);
      if (savedSizes) {
        const parsedSizes = JSON.parse(savedSizes);
        setColumnSizing(parsedSizes);
      } else {
        const initialSizing: Record<string, number> = {};
        columns.forEach((column) => {
          if (column.className) {
            const widthMatch = column.className.match(/w-(\d+)/);
            if (widthMatch) {
              initialSizing[column.field] = parseInt(widthMatch[1]);
            } else {
              initialSizing[column.field] = getDefaultColumnWidth(column);
            }
          } else {
            initialSizing[column.field] = getDefaultColumnWidth(column);
          }
        });
        setColumnSizing(initialSizing);
      }
    } catch (error) {
      const initialSizing: Record<string, number> = {};
      columns.forEach((column) => {
        initialSizing[column.field] = getDefaultColumnWidth(column);
      });
      setColumnSizing(initialSizing);
    }
  }, [columns, localStorageKey]);

  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      try {
        localStorage.setItem(localStorageKey, JSON.stringify(columnSizing));
      } catch (error) {
        console.warn('Failed to save column sizes to localStorage:', error);
      }
    }
  }, [columnSizing, localStorageKey]);

  const getDefaultColumnWidth = (column: Column): number => {
    switch (column.format) {
      case 'icon':
        return 35;
      case 'checkbox':
        return 35;
      case 'payment':
        return 35;
      case 'status':
        return 35;
      case 'xsm':
        return 35;
      default:
        return 220;
    }
  };

  const handleResizeStart = (e: React.MouseEvent, columnId: string) => {
    if (!enableColumnResize) return;

    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth =
      columnSizing[columnId] ||
      getDefaultColumnWidth(columns.find((col) => col.field === columnId)!);

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + deltaX);

      setColumnSizing((prev) => ({
        ...prev,
        [columnId]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const tableColumns = useMemo(() => {
    return columns.map((column) => {
      const sortField = column.sortField || column.field;

      const baseColumn = columnHelper.accessor(sortField as any, {
        id: column.field,
        size: columnSizing[column.field] || getDefaultColumnWidth(column),
        header: ({ column: tableColumn }) => (
          <div className="mo-table-head">
            <div className="mo-table-header-content">
              {filterVisibility[column.field] ? (
                <input
                  className="mo-table-filter-input"
                  placeholder={t(column.label)}
                  value={(tableColumn.getFilterValue() as string) ?? ''}
                  onChange={(e) => {
                    tableColumn.setFilterValue(e.target.value);
                    setTimeout(() => updateVisibleRows(), 0);
                  }}
                />
              ) : (
                <span className="text-nowrap">{t(column.label)}</span>
              )}
            </div>
            <div className="mo-table-actions">
              {column.filter && (
                <span
                  className="mo-table-filter"
                  onClick={() =>
                    setFilterVisibility((prev) => ({
                      ...prev,
                      [column.field]: !prev[column.field],
                    }))
                  }
                >
                  <FontAwesomeIcon
                    className={filterVisibility[column.field] ? 'text-contrast' : ''}
                    icon={faFilter}
                  />
                </span>
              )}
              {!props.hideSort && tableColumn.getCanSort() && (
                <span
                  className="mo-table-sort cur-poi"
                  onClick={tableColumn.getToggleSortingHandler()}
                >
                  {tableColumn.getIsSorted() === 'asc' && (
                    <FontAwesomeIcon className="mo-sort-active" icon={faSortUp} />
                  )}
                  {tableColumn.getIsSorted() === 'desc' && (
                    <FontAwesomeIcon className="mo-sort-active" icon={faSortDown} />
                  )}
                  {!tableColumn.getIsSorted() && (
                    <FontAwesomeIcon icon={faSort} className="mo-table-sort-inactive" />
                  )}
                </span>
              )}
            </div>
            {enableColumnResize && (
              <div
                className="mo-table-resizer"
                onMouseDown={(e) => handleResizeStart(e, column.field)}
                style={{
                  position: 'absolute',
                  right: -6,
                  top: 0,
                  bottom: 0,
                  width: 8,
                  cursor: 'col-resize',
                  backgroundColor: isResizing ? 'var(--color-primary)' : 'transparent',
                  zIndex: 1,
                }}
              />
            )}
          </div>
        ),
        cell: (info) => {
          const row = info.row.original;
          const rowIndex = info.row.index;
          const value = row[column.field];

          // Icon format
          if (column.format === 'icon') {
            return (
              <span>
                <FontAwesomeIcon
                  icon={getIcon(value)}
                  className={column.helpField ? row[column.helpField]?.split('|')[0] : ''}
                />
                {column.helpField && row[column.helpField]?.split('|').length === 2 && (
                  <span className="ml-5">{row[column.helpField]?.split('|')[1]}</span>
                )}
              </span>
            );
          }

          // Payment format
          if (column.format === 'payment' && value) {
            return (
              <span className={`payment ${getPayment(value)}`}>
                <FontAwesomeIcon icon={faCcVisa as any} />
              </span>
            );
          }

          // Status format
          if (column.format === 'status') {
            return <span className={`mo-status ${getStatus(value)}`}>{value}</span>;
          }

          // Segment format
          if (column.format === 'segment') {
            return (
              <span
                className="segment cur-poi text-underline text-contrast"
                onClick={() => onDoubleClick(row.segmentId)}
                style={{ backgroundColor: row.segmentColor }}
              >
                <span>{value}</span>
              </span>
            );
          }

          // Checkbox format
          if (column.format === 'checkbox' && value) {
            return (
              <div className="flex-center">
                <Checkbox checked={value} disabled={true} />
              </div>
            );
          }

          // Show more format
          if (column.format === 'show_more') {
            const showMoreKey = rowIndex + column.field;
            return (
              <div
                className={`mo-table-show-more${showMore.includes(showMoreKey) ? ' active' : ''}`}
              >
                {showMore.includes(showMoreKey) ? (
                  <div>
                    <div
                      className="cur-poi"
                      onClick={() => setShowMore((prev) => prev.filter((it) => it !== showMoreKey))}
                    >
                      <FontAwesomeIcon icon={faMinus} className="mr-5" />
                      {t('show_less')}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: value }} />
                  </div>
                ) : (
                  <div
                    className="cur-poi"
                    onClick={() => setShowMore((prev) => [...prev, showMoreKey])}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-5" />
                    {t('show_more')}
                  </div>
                )}
              </div>
            );
          }

          // HTML format
          if (column.format === 'html' && column.helpField) {
            return <div dangerouslySetInnerHTML={{ __html: row[column.helpField] }} />;
          }

          // JSX format
          if (column.format === 'jsx' && column.helpField) {
            return row[column.helpField];
          }

          // Default format
          return <span>{value}</span>;
        },
        enableSorting: !props.hideSort,
        enableColumnFilter: column.filter,
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue(columnId);
          const b = rowB.getValue(columnId);

          if (typeof a === 'string' && typeof b === 'string') {
            const dateA = parse(a, 'dd/MM/yy', new Date());
            const dateB = parse(b, 'dd/MM/yy', new Date());

            if (isValid(dateA) && isValid(dateB)) {
              return dateA.getTime() - dateB.getTime();
            }
          }

          if (a instanceof Date && b instanceof Date) {
            return a.getTime() - b.getTime();
          }

          if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
          }

          if (typeof a === 'boolean' && typeof b === 'boolean') {
            return a === b ? 0 : a ? 1 : -1;
          }

          if (a == null && b == null) return 0;
          if (a == null) return -1;
          if (b == null) return 1;

          return String(a).localeCompare(String(b));
        },
      });

      return baseColumn;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    columns,
    t,
    filterVisibility,
    props.hideSort,
    showMore,
    onDoubleClick,
    columnSizing,
    enableColumnResize,
    isResizing,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredData = useMemo(() => {
    if (!debouncedSearchFilters.orderSupplierPNR &&
      !debouncedSearchFilters.orderBackOfficeId &&
      !debouncedSearchFilters.orderPaxName &&
      !debouncedSearchFilters.orderItem) {
      return props.data;
    }
    return props.data.filter((row) => matchesTableDataSearchFilters(row, debouncedSearchFilters));
  }, [props.data, debouncedSearchFilters]);

  const table = useReactTable({
    data: filteredData,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter: mainFilter || globalFilter,
      columnSizing,
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: mainFilter ? customGlobalFilterFn : 'includesString',
    columnResizeMode: 'onChange' as ColumnResizeMode,
  });

  const updateVisibleRows = () => {
    const visibleSegmentIds = table.getRowModel().rows.map((row) => row.original.segmentId);
    setVisibleRows(visibleSegmentIds);
    const allRows = table.getRowModel().rows;
    setHasMore(allRows.length > total);
  };

  useEffect(() => {
    if (!listFilterVersion) return;
    const update = getOrdersListFilter();
    if (update.clearColumns) {
      setColumnFilters([]);
      setFilterVisibility({});
    }
    if (!update.columns.length) return;
    setColumnFilters((prev) => {
      const ids = new Set(update.columns.map((column) => column.id));
      const kept = prev.filter((column) => !ids.has(column.id));
      update.columns.forEach((column) => {
        if (column.value) kept.push({ id: column.id, value: column.value });
      });
      return kept;
    });
    setFilterVisibility((prev) => {
      const next = { ...prev };
      update.columns.forEach((column) => {
        next[column.id] = !!column.value;
      });
      return next;
    });
  }, [listFilterVersion]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const allRows = table.getRowModel().rows;
      setHasMore(allRows.length > total);
    }, 0);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting, columnFilters, globalFilter, mainFilter, props.data.length, total, debouncedSearchFilters]);

  const clearFilters = () => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setColumnFilters([]);
    setGlobalFilter('');
    setFilterVisibility({});
  };
  const getRowId = (row: { original: { segmentId?: number; orderLogId?: number } }) =>
    row.original.segmentId ?? row.original.orderLogId;

  const selectRecordByDelta = (delta: 1 | -1) => {
    const allRows = table.getRowModel().rows;
    if (!allRows.length) return;

    const currentIndex = allRows.findIndex(
      (row) =>
        row.original.segmentId === activeIdRef.current ||
        row.original.orderLogId === activeIdRef.current
    );

    const nextIndex =
      currentIndex === -1
        ? delta === 1
          ? 0
          : Math.min(total, allRows.length) - 1
        : currentIndex + delta;

    if (nextIndex < 0 || nextIndex >= allRows.length) return;

    if (nextIndex >= total) {
      setTotal((prev) => Math.max(prev, nextIndex + 1));
    }

    const nextId = getRowId(allRows[nextIndex]);
    if (nextId == null) return;

    activeIdRef.current = nextId;
    props.onClick?.(nextId);
  };

  useLayoutEffect(() => {
    if (props.activeId == null) return;
    const rows = table.getRowModel().rows;
    const index = rows.findIndex((row) => row.original.segmentId === props.activeId);
    if (index >= total) {
      setTotal(index + 1);
      return;
    }
    const rowEl = tableRef.current?.querySelector(':scope > tbody > tr.active') as HTMLElement | null;
    if (!rowEl) return;
    keepRowInView(rowEl, wrapperRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeId, total]);

  const fetchData = () => {
    const allRows = table.getRowModel().rows;
    if (allRows.length <= total) return;

    setTotal((prevTotal) => {
      const newTotal = prevTotal + count;
      setHasMore(newTotal < allRows.length);
      return newTotal;
    });
  };

  const visibleRows = table.getRowModel().rows.slice(0, total);

  return (
    <div className="mo-table-wr mo-main-table-wr" ref={wrapperRef}>
      {(columnFilters.length > 0 || globalFilter) && (
        <span className="text-xsm color-danger cur-poi mo-table-clear" onClick={clearFilters}>
          {t('filter.clear')}
        </span>
      )}

      <InfiniteScroll
        loadMore={fetchData}
        hasMore={hasMore}
        useWindow={false}
        loader={<div key={0}></div>}
      >
        <table
          ref={tableRef}
          className="mo-table"
          tabIndex={0}
          style={{ tableLayout: 'fixed' }}
          onKeyDown={(e) => {
            const tag = (e.target as HTMLElement).tagName.toLowerCase();
            if (tag === 'input' || tag === 'textarea') return;

            if (e.key === 'ArrowDown') {
              e.preventDefault();
              selectRecordByDelta(1);
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              selectRecordByDelta(-1);
            }
          }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={
                      'position-relative ' +
                      (columns.find((col) => col.field === header.id)?.className || '')
                    }
                    style={{
                      width: header.getSize(),
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.id}
                onDoubleClick={() => onDoubleClick(row.original.segmentId ?? row.original.orderLogId)}
                onClick={() => props.onClick?.(row.original.segmentId ?? row.original.orderLogId)}
                className={
                  (props.activeId === row.original.segmentId ||
                    props.activeId === row.original.orderLogId
                    ? 'active'
                    : '') +
                  (parse(row.original.clx || '', 'dd/MM/yy', new Date()) < addDays(new Date(), 2) &&
                    !row.original.voucher
                    ? ' mo-row-danger'
                    : '')
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={columns.find((col) => col.field === cell.column.id)?.className}
                    title={cell.getValue() ? String(cell.getValue()) : undefined}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </InfiniteScroll>

    </div>
  );
};

export default TableTanStack;
